import React, { useCallback, useEffect, useRef, useState } from "react";
import { StyleSheet, View } from "react-native";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import BlankLayout from "../../../layouts/BlankLayout";
import Loader from "../../../components/common/status/Loader/Loader";
import ErrorLoader from "../../../components/common/status/ErrorLoader/ErrorLoader";
import type { RootStackParamList } from "../../../navigation/navigation.types";
import AssessmentMenu from "../../../components/navigation/AssessmentMenu/AssessmentMenu";
import AssessmentBeginModal from "../sharedFiles/AssessmentBeginModal";
import AssessmentCompleteModal from "../sharedFiles/AssessmentCompleteModal";
import ExitBeforeCompletedModal from "../sharedFiles/ExitBeforeCompletedModal";
import CursorLeavesWindowModal from "../sharedFiles/CursorLeavesWindowModal";
import AssessmentInProgressModal from "../sharedFiles/AssessmentInProgressModal";
import AssessmentErrorModal from "../sharedFiles/AssessmentErrorModal";
import QuestionCounter from "../sharedFiles/AssessmentQuestionCounter";
import {
  continueAssessmentMcq,
  endSseAssessment,
  getCurrentMcqQuestion,
  getSseAssessmentTimer,
  startSseAssessment,
} from "../sharedFiles/AssessmentUtils";
import { formatCountdownFromSeconds } from "../../../utils/timeUtils";
import {
  useGetAssessmentQuery,
  useGetCourseCoreQuery,
  useGetRunningAssessmentSubmissionByUserQuery,
} from "../../../graphql/generated/graphql";
import McqAssessmentQuestionCard from "../../../components/assessment/McqAssessmentQuestionCard/McqAssessmentQuestionCard";

interface McqAssessmentProps {
  assessmentId: string;
  courseId: string;
  integrationId: string;
  moduleId?: string;
}

const McqAssessment = ({ assessmentId, courseId, integrationId, moduleId }: McqAssessmentProps) => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const remainingSecondsRef = useRef(0);

  const [beginModalVisible, setBeginModalVisible] = useState(false);
  const [completeModalVisible, setCompleteModalVisible] = useState(false);
  const [exitModalVisible, setExitModalVisible] = useState(false);
  const [anotherAssessmentInProgressModalVisible, setAnotherAssessmentInProgressModalVisible] = useState(false);
  const [cursorLeavesWindowModalVisible, setCursorLeavesWindowModalVisible] = useState(false);
  const [assessmentErrorModalVisible, setAssessmentErrorModalVisible] = useState(false);
  const [isStartingAssessment, setIsStartingAssessment] = useState(false);
  const [isSubmittingAnswer, setIsSubmittingAnswer] = useState(false);
  const [isTerminatingAssessment, setIsTerminatingAssessment] = useState(false);
  const [displayTime, setDisplayTime] = useState<string | null>(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState<number | null>(null);
  const [currentQuestionId, setCurrentQuestionId] = useState<string | null>(null);
  const [currentQuestionText, setCurrentQuestionText] = useState<string | null>(null);
  const [currentChoices, setCurrentChoices] = useState<string[]>([]);
  const [currentQuestionHasMultipleCorrectAnswers, setCurrentQuestionHasMultipleCorrectAnswers] = useState(false);
  const [selectedChoiceIndices, setSelectedChoiceIndices] = useState<number[]>([]);

  const { refetch: refetchCourseData } = useGetCourseCoreQuery({
    variables: {
      id: courseId,
    },
  });

  const {
    data: assessment,
    loading: assessmentLoading,
    error: assessmentError,
    refetch: refetchAssessment,
  } = useGetAssessmentQuery({
    variables: {
      assessmentId,
    },
    skip: !assessmentId,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: assessmentSubmission,
    loading: assessmentSubmissionLoading,
    error: assessmentSubmissionError,
    refetch: refetchAssessmentSubmission,
  } = useGetRunningAssessmentSubmissionByUserQuery({
    skip: !assessmentId,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const assessmentSubmissionData = assessmentSubmission?.getRunningAssessmentSubmissionByUser?.data;
  const activeAssessmentId = assessmentSubmissionData?.assessment?.id;
  const assessmentSubmissionIsRunning = assessmentSubmissionData?.status === "RUNNING";
  const assessmentIsRunning =
    !!activeAssessmentId && assessmentSubmissionIsRunning && activeAssessmentId === assessmentId;
  const assessmentSubmissionNotExistsInDatabase = !assessmentSubmissionData;
  const userAlreadyHasAnotherRunningAssessment = !!activeAssessmentId && activeAssessmentId !== assessmentId;
  const assessmentTimeLimit = assessment?.getAssessment?.data?.timeLimit ?? null;
  const assessmentNumberOfQuestions = assessment?.getAssessment?.data?.numberOfQuestions ?? null;

  const cleanupIntervals = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
      syncIntervalRef.current = null;
    }
  }, []);

  const syncTimerWithServer = useCallback(async () => {
    const timerData = await getSseAssessmentTimer();
    if (!timerData || timerData.remainingSec === undefined) {
      return;
    }
    remainingSecondsRef.current = timerData.remainingSec;
    setDisplayTime(formatCountdownFromSeconds(timerData.remainingSec));
  }, []);

  const startTimer = useCallback(
    (remainingSec: number) => {
      cleanupIntervals();
      remainingSecondsRef.current = remainingSec;
      setDisplayTime(formatCountdownFromSeconds(remainingSec));

      intervalRef.current = setInterval(() => {
        if (remainingSecondsRef.current <= 0) {
          cleanupIntervals();
          return;
        }
        remainingSecondsRef.current = Math.max(0, remainingSecondsRef.current - 1);
        setDisplayTime(formatCountdownFromSeconds(remainingSecondsRef.current));
      }, 1000);

      syncIntervalRef.current = setInterval(() => {
        syncTimerWithServer();
      }, 5000);
    },
    [cleanupIntervals, syncTimerWithServer]
  );

  const hydrateCurrentQuestion = useCallback(async () => {
    const response = await getCurrentMcqQuestion(assessmentId, () => setAssessmentErrorModalVisible(true));
    if (!response) {
      setIsStartingAssessment(false);
      return;
    }

    if (response.completed) {
      setCompleteModalVisible(true);
      setCurrentQuestionId(null);
      setCurrentQuestionText(null);
      setCurrentChoices([]);
      setCurrentQuestionHasMultipleCorrectAnswers(false);
      setSelectedChoiceIndices([]);
      cleanupIntervals();
      setDisplayTime("00:00");
      return;
    }

    setQuestionIndex(response.questionIndex);
    setTotalQuestions(response.totalQuestions);
    setCurrentQuestionId(response.question?.id ?? null);
    setCurrentQuestionText(response.question?.question ?? null);
    setCurrentChoices(response.question?.choices ?? []);
    setCurrentQuestionHasMultipleCorrectAnswers(response.question?.hasMultipleCorrectAnswers ?? false);
    setSelectedChoiceIndices([]);
  }, [assessmentId, cleanupIntervals]);

  const handleExitPage = useCallback(() => {
    cleanupIntervals();
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }

    if (moduleId) {
      navigation.navigate("Custom Module Student", {
        module_id: moduleId,
        course_id: courseId,
        integration_id: typeof integrationId === "string" ? integrationId : courseId,
      });
      return;
    }

    navigation.navigate("Custom Course Student", {
      course_id: courseId,
      integration_id: integrationId,
    });
  }, [cleanupIntervals, navigation, moduleId, courseId, integrationId]);

  const initializeRunningState = useCallback(async () => {
    const timerData = await getSseAssessmentTimer();
    if (timerData && timerData.remainingSec !== undefined) {
      startTimer(timerData.remainingSec);
      if (timerData.questionIndex !== undefined) {
        setQuestionIndex((timerData.questionIndex ?? 0) + 1);
      }
    } else if (assessmentTimeLimit) {
      startTimer(assessmentTimeLimit * 60);
    }
    await hydrateCurrentQuestion();
  }, [assessmentTimeLimit, hydrateCurrentQuestion, startTimer]);

  useFocusEffect(
    useCallback(() => {
      if (assessmentSubmissionLoading || assessmentLoading) {
        return;
      }

      const initializePage = async () => {
        if (userAlreadyHasAnotherRunningAssessment) {
          setAnotherAssessmentInProgressModalVisible(true);
          return;
        }

        if (assessmentIsRunning) {
          setBeginModalVisible(false);
          await initializeRunningState();
          return;
        }

        if (assessmentSubmissionNotExistsInDatabase) {
          setBeginModalVisible(true);
        }
      };

      initializePage();
    }, [
      assessmentSubmissionLoading,
      assessmentLoading,
      userAlreadyHasAnotherRunningAssessment,
      assessmentIsRunning,
      assessmentSubmissionNotExistsInDatabase,
      initializeRunningState,
    ])
  );

  useFocusEffect(
    useCallback(() => {
      if (!assessmentId) {
        return;
      }
      refetchAssessment();
      refetchAssessmentSubmission();
    }, [assessmentId])
  );

  useEffect(() => {
    return () => {
      cleanupIntervals();
    };
  }, [cleanupIntervals]);

  const handleVisibilityChange = useCallback(() => {
    if (!assessmentIsRunning) {
      return;
    }
    if (document.hidden) {
      setCursorLeavesWindowModalVisible(true);
    }
  }, [assessmentIsRunning]);

  const handleMouseLeave = useCallback(
    (event) => {
      if (!assessmentIsRunning) {
        return;
      }
      const { clientX, clientY } = event;
      const leftEdge = clientX <= 0;
      const rightEdge = clientX >= window.innerWidth;
      const topEdge = clientY <= 0;
      const bottomEdge = clientY >= window.innerHeight;

      if (leftEdge || rightEdge || topEdge || bottomEdge) {
        setCursorLeavesWindowModalVisible(true);
      }
    },
    [assessmentIsRunning]
  );

  const handleSelectChoice = useCallback(
    (choiceIndex: number) => {
      setSelectedChoiceIndices((prev) => {
        if (!currentQuestionHasMultipleCorrectAnswers) {
          return [choiceIndex];
        }
        return prev.includes(choiceIndex) ? prev.filter((index) => index !== choiceIndex) : [...prev, choiceIndex];
      });
    },
    [currentQuestionHasMultipleCorrectAnswers]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!assessmentIsRunning || cursorLeavesWindowModalVisible || isTerminatingAssessment || isSubmittingAnswer) {
        return;
      }

      const numericKey = Number(event.key);
      if (Number.isInteger(numericKey) && numericKey >= 1 && numericKey <= currentChoices.length) {
        handleSelectChoice(numericKey - 1);
      }
    },
    [
      handleSelectChoice,
      assessmentIsRunning,
      cursorLeavesWindowModalVisible,
      isTerminatingAssessment,
      isSubmittingAnswer,
      currentChoices.length,
    ]
  );

  useFocusEffect(
    useCallback(() => {
      document.addEventListener("visibilitychange", handleVisibilityChange);
      document.addEventListener("mouseleave", handleMouseLeave);
      document.addEventListener("keydown", handleKeyDown);

      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        document.removeEventListener("mouseleave", handleMouseLeave);
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, [handleMouseLeave, handleVisibilityChange, handleKeyDown])
  );

  const handleBeginAssessment = async () => {
    if (assessmentIsRunning || isStartingAssessment) {
      return;
    }

    setIsStartingAssessment(true);
    const response = await startSseAssessment(assessmentId, () => {
      setAssessmentErrorModalVisible(true);
      setIsStartingAssessment(false);
    });

    if (!response) {
      setIsSubmittingAnswer(false);
      return;
    }

    const remainingSec = response.remainingSec ?? response.durationSec;
    startTimer(remainingSec);
    await refetchAssessmentSubmission();
    await hydrateCurrentQuestion();
    setBeginModalVisible(false);
    setIsStartingAssessment(false);
  };

  const handleSubmitAnswer = async () => {
    if (selectedChoiceIndices.length === 0 || isSubmittingAnswer || !currentQuestionId) {
      return;
    }

    setIsSubmittingAnswer(true);
    const response = await continueAssessmentMcq(assessmentId, selectedChoiceIndices, () => {
      setAssessmentErrorModalVisible(true);
      setIsSubmittingAnswer(false);
    });

    if (!response) {
      return;
    }

    if (response.completed) {
      cleanupIntervals();
      setDisplayTime("00:00");
      setQuestionIndex(response.questionIndex);
      setTotalQuestions(response.totalQuestions);
      setCurrentQuestionId(null);
      setCurrentQuestionText(null);
      setCurrentChoices([]);
      setCurrentQuestionHasMultipleCorrectAnswers(false);
      setSelectedChoiceIndices([]);
      setIsSubmittingAnswer(false);
      await refetchCourseData();
      setCompleteModalVisible(true);
      return;
    }

    setQuestionIndex(response.questionIndex);
    setTotalQuestions(response.totalQuestions);
    setCurrentQuestionId(response.question?.id ?? null);
    setCurrentQuestionText(response.question?.question ?? null);
    setCurrentChoices(response.question?.choices ?? []);
    setCurrentQuestionHasMultipleCorrectAnswers(response.question?.hasMultipleCorrectAnswers ?? false);
    setSelectedChoiceIndices([]);
    setIsSubmittingAnswer(false);
  };

  const handleTerminateAssessment = async () => {
    setExitModalVisible(false);
    setIsTerminatingAssessment(true);
    await endSseAssessment(() => {
      setAssessmentErrorModalVisible(true);
      setIsTerminatingAssessment(false);
    });
    await refetchCourseData();
    setIsTerminatingAssessment(false);
    handleExitPage();
  };

  const handleExitButtonPress = () => {
    if (assessmentIsRunning) {
      setExitModalVisible(true);
      return;
    }
    handleExitPage();
  };

  const handleConfirmCompleteModal = () => {
    setCompleteModalVisible(false);
    handleExitPage();
  };

  if (assessmentLoading || assessmentSubmissionLoading) {
    return (
      <BlankLayout>
        <View style={styles.loaderContainer}>
          <Loader />
        </View>
      </BlankLayout>
    );
  }

  if (assessmentError || assessmentSubmissionError || !assessment?.getAssessment?.data) {
    return (
      <BlankLayout>
        <View style={styles.loaderContainer}>
          <ErrorLoader message="Something went wrong! Try reloading the page." />
        </View>
      </BlankLayout>
    );
  }

  if (!assessmentNumberOfQuestions || assessmentNumberOfQuestions <= 0) {
    return (
      <BlankLayout>
        <View style={styles.loaderContainer}>
          <ErrorLoader message="This assessment is not enabled." />
        </View>
      </BlankLayout>
    );
  }

  const moduleName = assessment.getAssessment.data.moduleHasAssessment?.title;

  return (
    <BlankLayout>
      <View style={styles.pageContainer}>
        <AssessmentMenu
          handleExitPress={handleExitButtonPress}
          remainingTime={displayTime ?? "--:--"}
          moduleName={moduleName}
          hasMicrophoneControls={false}
          timeIsLoading={!displayTime}
          isSseAssessment={true}
        />

        <View style={styles.contentContainer}>
          {currentQuestionText ? (
            <>
              <QuestionCounter
                questionIndex={questionIndex}
                totalQuestions={totalQuestions ?? assessmentNumberOfQuestions}
              />
              <McqAssessmentQuestionCard
                question={currentQuestionText}
                choices={currentChoices}
                showHasMultipleAnswersHint={currentQuestionHasMultipleCorrectAnswers}
                selectedChoiceIndices={selectedChoiceIndices}
                onSelectChoice={handleSelectChoice}
                onSubmit={handleSubmitAnswer}
                isSubmitting={isSubmittingAnswer}
                disabled={isTerminatingAssessment}
              />
            </>
          ) : (
            <Loader />
          )}
        </View>
      </View>

      <AssessmentBeginModal
        visible={beginModalVisible}
        onCancel={() => {
          setBeginModalVisible(false);
          handleExitPage();
        }}
        onConfirm={handleBeginAssessment}
        defaultTime={assessmentTimeLimit ?? 0}
        defaultQuestionsCount={assessmentNumberOfQuestions ?? 0}
        isLoading={isStartingAssessment}
        selectedDeviceId=""
        onDeviceSelect={() => {}}
        skipLivekitCheck={true}
        showMicrophoneSelector={false}
      />
      <AssessmentCompleteModal visible={completeModalVisible} onConfirm={handleConfirmCompleteModal} />
      <ExitBeforeCompletedModal
        visible={exitModalVisible}
        onConfirm={handleTerminateAssessment}
        onCancel={() => setExitModalVisible(false)}
      />
      <CursorLeavesWindowModal
        visible={cursorLeavesWindowModalVisible}
        onConfirm={() => setCursorLeavesWindowModalVisible(false)}
      />
      <AssessmentInProgressModal
        visible={anotherAssessmentInProgressModalVisible}
        onConfirm={() => {
          setAnotherAssessmentInProgressModalVisible(false);
          handleExitPage();
        }}
      />
      <AssessmentErrorModal
        visible={assessmentErrorModalVisible}
        onConfirm={() => {
          setAssessmentErrorModalVisible(false);
          handleExitPage();
        }}
      />
    </BlankLayout>
  );
};

const styles = StyleSheet.create({
  pageContainer: {
    flex: 1,
    backgroundColor: "#F2F4F7",
  },
  contentContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    gap: 18,
    padding: 20,
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
});

export default McqAssessment;
